import type { ReactNode } from 'react';
import type { GlobalPrivilege } from '@guest/shared';
import { Icon } from '../lib/icons.js';
import { useHouse } from '../lib/store.js';
import { ALARM_LABELS } from '../lib/entity.js';

export function AlarmCard({
  entityId,
  globals,
}: {
  entityId: string;
  globals: GlobalPrivilege[];
}): ReactNode {
  const { states, act, pending } = useHouse();
  const state = states.get(entityId);
  const value = state?.state ?? 'unknown';
  const busy = pending.has(entityId);

  const canDisarm = globals.includes('alarm_disarm');
  const canArm = globals.includes('alarm_arm');

  const armed = value.startsWith('armed');
  const triggered = value === 'triggered';
  // `arming` is the exit delay, `pending` the entry delay. Both are in-flight,
  // so the arm options must not reappear underneath them.
  const inFlight = value === 'arming' || value === 'pending' || value === 'disarming';
  const disarmed = value === 'disarmed';

  const tone = triggered ? 'alert' : armed || inFlight ? 'caution' : disarmed ? 'positive' : undefined;

  const blurb = triggered
    ? 'The alarm is sounding. Disarm it if this was you.'
    : value === 'arming'
      ? 'Exit delay running — leave now.'
      : value === 'pending'
        ? 'Entry delay running — disarm it.'
        : value === 'disarming'
          ? 'Disarming…'
          : armed
            ? 'The house alarm is set.'
            : disarmed
              ? 'The house alarm is off.'
              : 'The alarm state is unknown.';

  return (
    <section className="glass hero">
      <div className="hero-top">
        <span className="hero-glyph" {...(tone ? { 'data-tone': tone } : {})}>
          <Icon name={armed || triggered || inFlight ? 'shield' : 'shield-check'} size={22} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="hero-title">{ALARM_LABELS[value] ?? value}</div>
          <div className="hero-sub">{blurb}</div>
        </div>
      </div>

      <div className="hero-actions">
        {canDisarm && !disarmed && (
          <button
            type="button"
            className={`btn ${triggered ? 'btn-alert' : 'btn-primary'}`}
            disabled={busy}
            onClick={() => void act({ action: 'alarm_disarm', entityId })}
          >
            <Icon name="unlock" size={17} />
            {value === 'arming' ? 'Cancel' : 'Disarm'}
          </button>
        )}

        {canArm && disarmed && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => void act({ action: 'alarm_arm_away', entityId })}
          >
            <Icon name="shield" size={17} />
            Arm the alarm
          </button>
        )}

        {!canArm && !canDisarm && (
          <span className="ctrl-sub">You can see the alarm but not change it.</span>
        )}
        {canDisarm && !canArm && disarmed && (
          <span className="ctrl-sub">You can disarm it if it goes off.</span>
        )}
      </div>

    </section>
  );
}
